import React from 'react';
import { Grid, Typography, Box, Paper, alpha, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { AssessmentCard } from './AssessmentCard';
import { PreAssessmentCardsProps, PreAssessmentType } from './types';

export const PreAssessmentCards: React.FC<PreAssessmentCardsProps> = ({
  preHealthConsultSubmitted,
  healthConsultSubmitted,
  intakeFormSubmitted,
  onAssessmentClick,
}) => {
  const theme = useTheme();

  const assessments: Array<{
    type: PreAssessmentType;
    title: string;
    submitted: boolean;
    description: string;
  }> = [
    {
      type: 'Pre-Health Consult',
      title: 'Pre-Health Consult',
      submitted: preHealthConsultSubmitted,
      description: 'Initial health questionnaire and screening',
    },
    {
      type: 'Health Consult',
      title: 'Health Consult',
      submitted: healthConsultSubmitted,
      description: 'Comprehensive health consultation',
    },
    {
      type: 'Intake Form',
      title: 'Intake Form',
      submitted: intakeFormSubmitted,
      description: 'Client information and goals',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const completedCount = assessments.filter(a => a.submitted).length;
  const completionPercentage = (completedCount / assessments.length) * 100;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.primary.light, 0.01)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Progress Bar Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${completionPercentage}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={{
            height: '100%',
            backgroundColor: theme.palette.primary.main,
          }}
        />
      </Box>

      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" fontWeight={600}>
          Pre-Assessment Data
        </Typography>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        >
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              px: 2,
              py: 0.5,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {completedCount} / {assessments.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              completed
            </Typography>
          </Box>
        </motion.div>
      </Box>

      <AnimatePresence>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid container spacing={2}>
            {assessments.map((assessment, index) => (
              <Grid item xs={12} sm={6} md={4} key={assessment.type}>
                <motion.div variants={itemVariants}>
                  <AssessmentCard
                    title={assessment.title}
                    isSubmitted={assessment.submitted}
                    onClick={() => onAssessmentClick(assessment.type)}
                    isClickable={true}
                    category={assessment.type}
                  />
                  {assessment.submitted && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ delay: 0.3 }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: 'block', px: 1 }}
                      >
                        {assessment.description}
                      </Typography>
                    </motion.div>
                  )}
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </AnimatePresence>
    </Paper>
  );
};